"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTheme } from "next-themes";
import { useTranslation } from "@/lib/i18n/context";
import { useAuth } from "@/lib/auth/context";
import { Button } from "@/components/ui/button";
import { Sun, Moon, Monitor, Settings, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";

type NavLink = { href: string; label: string };

function getNavLinks(role: string | undefined, t: (k: string) => string): NavLink[] {
  if (role === "commercial") {
    return [
      { href: "/", label: t("nav.dashboard") },
      { href: "/taches", label: t("nav.taches") },
      { href: "/properties", label: t("nav.properties") },
      { href: "/a-arbitrer", label: t("nav.aArbitrer") },
      { href: "/outreach", label: t("nav.outreach") },
      { href: "/valides", label: t("nav.valides") },
      { href: "/archives", label: t("nav.archives") },
      { href: "/mes-sms", label: t("nav.mesSms") },
    ];
  }
  if (role === "manager") {
    return [
      { href: "/", label: t("nav.home") },
      { href: "/manager", label: t("nav.team") },
      { href: "/manager/prospection", label: "Prospection" },
      { href: "/manager/scraping", label: "Scraping" },
      { href: "/properties", label: t("nav.properties") },
      { href: "/a-arbitrer", label: t("nav.aArbitrer") },
      { href: "/outreach", label: t("nav.outreach") },
      // Les biens validés : le manager suit l'aboutissement du travail de son
      // équipe, et y trouve le transfert vers ImmoImpact.
      { href: "/valides", label: t("nav.valides") },
      { href: "/mes-sms", label: t("nav.mesSms") },
    ];
  }
  if (role === "admin") {
    return [
      { href: "/", label: t("nav.home") },
      { href: "/manager/prospection", label: "Prospection équipe" },
      { href: "/properties", label: t("nav.properties") },
      { href: "/a-arbitrer", label: t("nav.aArbitrer") },
      { href: "/outreach", label: t("nav.outreach") },
      { href: "/valides", label: t("nav.valides") },
      { href: "/analytics", label: "Analytics" },
      { href: "/mes-sms", label: t("nav.mesSms") },
    ];
  }
  return [];
}

function useArbitrationCount(enabled: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    async function fetchCount() {
      try {
        const res = await fetch("/api/properties/arbitration-count");
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setCount(data.count ?? 0);
      } catch {
        // silently ignore
      }
    }

    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [enabled]);

  return count;
}

function usePendingReviewCount(enabled: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    async function fetchCount() {
      try {
        const res = await fetch("/api/interactions/pending-review-count");
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setCount(data.count ?? 0);
      } catch {
        // silently ignore
      }
    }

    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [enabled]);

  return count;
}

export default function Nav() {
  const pathname = usePathname();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const pendingReviewCount = usePendingReviewCount(!loading && !!user);
  const arbitrationCount = useArbitrationCount(!loading && !!user);

  // Hide nav on login page
  if (pathname === "/login") return null;

  const links = getNavLinks(user?.role, t);
  const isSettings = pathname.startsWith("/settings");

  function cycleTheme() {
    if (theme === "light") setTheme("dark");
    else if (theme === "dark") setTheme("system");
    else setTheme("light");
  }

  return (
    <nav className="border-b border-primary/20 bg-primary text-primary-foreground dark:bg-card dark:text-card-foreground dark:border-border">
      <div className="mx-auto flex max-w-7xl items-center gap-6 px-6 py-3">
        <Link href="/" className="text-sm font-bold tracking-wide">
          Groupe Foncier de France
        </Link>

        {!loading && links.map(({ href, label }) => {
          const isActive = href === "/"
            ? pathname === "/"
            : href === "/manager"
              ? pathname === "/manager"
              : pathname.startsWith(href);

          const isOutreach = href === "/outreach";
          const isArbitrer = href === "/a-arbitrer";
          const badgeCount = isOutreach
            ? pendingReviewCount
            : isArbitrer
              ? arbitrationCount
              : 0;
          const showBadge = badgeCount > 0;

          return (
            <Link
              key={href}
              href={href}
              className={cn(
                "relative text-sm transition-colors",
                isActive
                  ? "font-medium text-primary-foreground dark:text-foreground"
                  : "text-primary-foreground/70 hover:text-primary-foreground dark:text-muted-foreground dark:hover:text-foreground"
              )}
            >
              {label}
              {showBadge && (
                <span className="absolute -top-2 -right-3 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white leading-none">
                  {badgeCount > 9 ? "9+" : badgeCount}
                </span>
              )}
            </Link>
          );
        })}

        <div className="ml-auto flex items-center gap-1">
          {user?.role === "admin" && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-accent"
              asChild
            >
              <Link href="/settings">
                <Settings
                  className={cn(
                    "h-4 w-4",
                    isSettings ? "text-primary-foreground dark:text-foreground" : ""
                  )}
                />
                <span className="sr-only">{t("nav.settings")}</span>
              </Link>
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={cycleTheme}
            className="h-8 w-8 text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-accent"
          >
            {mounted ? (
              theme === "dark" ? (
                <Moon className="h-4 w-4" />
              ) : theme === "light" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Monitor className="h-4 w-4" />
              )
            ) : (
              <span className="h-4 w-4" />
            )}
            <span className="sr-only">{t("nav.toggleTheme")}</span>
          </Button>

          {user && (
            <>
              <span className="text-xs text-primary-foreground/70 dark:text-muted-foreground px-1">
                {user.firstName} {user.lastName}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className="h-8 w-8 text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-accent"
                title={t("nav.logout")}
              >
                <LogOut className="h-4 w-4" />
                <span className="sr-only">{t("nav.logout")}</span>
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
